import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn } from '@app/store/actions';
import { vaultPasswordsService } from '@app/services';
import { ModalActionsComponent, PasslyCheckbox } from '@app/components';
import { IPasswordDetailsNoPwd, TranslationsTypes } from '@app/models';
import { hasMachineName, showGenericCredentials, snmpAuthPassword, snmpPrivacyPassword, communityPassword, genericErrorString, onPressEnterModal } from '@app/helpers';
import VaultPasswordComponent from '../components/vaultPassword.component';
import { Box } from '@material-ui/core';
import { FormMode } from '@app/helpers/enums';
import { confirm } from '@app/components/Modal/confirmModal';

export interface IReceivedData {
    passwordId: string;
    vaultId: string;
    dialogOpen: Function;
    handleRefresh?: Function;
}

export const UpdatePasswordDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [formValid, setFormValid] = useState(true);
    const [passwordDetails, setPasswordDetails] = useState<IPasswordDetailsNoPwd | undefined>(undefined);
    const [showMachineName, setShowMachineName] = useState<boolean>(false);
    const [showGenericCreds, setShowGenericCreds] = useState<boolean>(false);
    const [confirmSourceChange, setConfirmSourceChange] = useState<boolean>(false);
    const [newGenericPassword, setNewGenericPassword] = useState<string>('');
    const [newCommunityString, setNewCommunityString] = useState<string>('');
    const [newAuthPassword, setNewAuthPassword] = useState<string>('');
    const [newPrivacyPassword, setNewPrivacyPassword] = useState<string>('');
    const [confirmCredsChangedText, setConfirmCredsChangedText] = useState<TranslationsTypes>('CONFIRM_CREDS_CHANGED');

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        vaultPasswordsService.getVaultPasswordByPasswordIdNoPwd(props.vaultId, props.passwordId, handleError, handleSuccessGetData);
    }, []);

    useEffect(() => {
        if (!confirmSourceChange && passwordDetails?.AgentProfileId === null) {
            setFormValid(false);
        } else {
            if (showGenericCreds) {
                if (newGenericPassword !== '') {
                    setFormValid(true);
                } else {
                    setFormValid(false);
                }
            } else if (passwordDetails && passwordDetails.PasswordType === 'SNMPV3') {
                if (newAuthPassword !== '' || newPrivacyPassword !== '') {
                    setFormValid(true);
                } else {
                    setFormValid(false);
                }
            } else if (passwordDetails && passwordDetails.PasswordType === 'SNMP') {
                if (newCommunityString !== '') {
                    setFormValid(true);
                } else {
                    setFormValid(false);
                }
            } else {
                setFormValid(true);
            }
        }
    }, [confirmSourceChange, newGenericPassword, newCommunityString, newAuthPassword, newPrivacyPassword]);

    const handleSuccessGetData = (response: any) => {
        const value: IPasswordDetailsNoPwd = response.data;

        setPasswordDetails(value);
        setShowMachineName(hasMachineName(value.PasswordType));
        setShowGenericCreds(showGenericCredentials(value.PasswordType));

        if (value.AgentProfileId !== null) {
            setConfirmCredsChangedText('DO_NOT_SYNC');
        }
    };

    const handleSuccessSaveData = () => {
        dispatch(getLoaderBtn(false));
        props.dialogOpen(false);

        if (props.handleRefresh) {
            props.handleRefresh(true, passwordDetails?.DisplayName);
        }
    };

    const handleError = (error: any) => {
        dispatch(getLoaderBtn(false));
        setErrorMessage(genericErrorString(error));
    };

    const handleCancel = () => {
        props.dialogOpen(false);
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};
        jsonObject.VaultPassword = {};
        jsonObject.VaultPassword.VaultId = props.vaultId;
        jsonObject.VaultPassword.Password = newGenericPassword;
        jsonObject.VaultPassword.SnmpCommunityString = newCommunityString;

        if (passwordDetails !== undefined && passwordDetails.PasswordType === 'SNMPV3') {
            if (newAuthPassword === '') {
                jsonObject.VaultPassword.SnmpAuthenticationPassword = passwordDetails.SnmpAuthenticationPassword;
            }
            else {
                jsonObject.VaultPassword.SnmpAuthenticationPassword = newAuthPassword;
            }

            if (newPrivacyPassword === '') {
                jsonObject.VaultPassword.SnmpPrivacyPassword = passwordDetails.SnmpPrivacyPassword;
            }
            else {
                jsonObject.VaultPassword.SnmpPrivacyPassword = newPrivacyPassword;
            }
        }

        jsonObject.DoNotSyncPassword = passwordDetails !== undefined && passwordDetails.AgentProfileId !== null && confirmSourceChange;

        jsonObject.VaultPasswordId = props.passwordId;

        vaultPasswordsService.updatePasswordOnly(jsonObject, props.vaultId, handleSuccessSaveData, handleError);
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        switch (name) {
            case 'confirmSourceChange':
                confirmCheckboxChanged(checked);
                break;
            default:
                break;
        }
    };

    const confirmCheckboxChanged = (checked: any) => {
        setConfirmSourceChange(checked);

        if (passwordDetails?.AgentProfileId !== null) {
            confirm(translation('CONFIRM_NO_PWD_SYNC'),
                translation('PWSSYNC_YES'),
                translation('CANCEL'),
                translation('CONFIRM_NO_PWD_SYNC_HEADER')).then(
                    () => {
                        setConfirmSourceChange(true);
                    },
                    () => {
                        setConfirmSourceChange(false);
                    }
                );
        }
    };

    const generatePasswordGeneric = (generatedPwd: string) => {
        setNewGenericPassword(generatedPwd);
    };

    const generateCommunityString = (generatedPwd: string) => {
        setNewCommunityString(generatedPwd);
    };

    const generatePasswordAuth = (generatedPwd: string) => {
        setNewAuthPassword(generatedPwd);
    };

    const generatePasswordPrivacy = (generatedPwd: string) => {
        setNewPrivacyPassword(generatedPwd);
    };

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
            {passwordDetails && (
                <div className='update-password__dialog'>
                    <div className='update-password__dialog--top'>
                        {showGenericCreds && (
                            <>
                                <div>
                                    <span className='vault__password--password__label'>{translation('NEW_PASSWORD')}:</span>
                                    <VaultPasswordComponent
                                        autoFocus={true}
                                        defaultStyle={true}
                                        formMode={FormMode.Add}
                                        vaultId={passwordDetails.VaultId}
                                        passwordId={passwordDetails.Id}
                                        showPasswordStrength={true}
                                        showGenerateButton={true}
                                        returnGeneratedPassword={generatePasswordGeneric}
                                        showPlaceholder={false}
                                    />
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'SNMPV3' && (
                            <>
                                <div>
                                    <span className='vault__password--password__label'>{translation('NEW_AUTH_PASSWORD')}:</span>
                                    <VaultPasswordComponent
                                        autoFocus={true}
                                        defaultStyle={true}
                                        formMode={FormMode.Add}
                                        vaultId={passwordDetails.VaultId}
                                        passwordId={passwordDetails.Id}
                                        passwordWanted={snmpAuthPassword}
                                        showPasswordStrength={true}
                                        showGenerateButton={true}
                                        returnGeneratedPassword={generatePasswordAuth}
                                        showPlaceholder={false}
                                    />
                                </div>
                                <div>
                                    <span className='vault__password--password__label'>{translation('NEW_PRIVACY_PASSWORD')}:</span>
                                    <VaultPasswordComponent
                                        defaultStyle={true}
                                        formMode={FormMode.Add}
                                        vaultId={passwordDetails.VaultId}
                                        passwordId={passwordDetails.Id}
                                        passwordWanted={snmpPrivacyPassword}
                                        showPasswordStrength={true}
                                        showGenerateButton={true}
                                        returnGeneratedPassword={generatePasswordPrivacy}
                                        showPlaceholder={false}
                                    />
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'SNMP' && (
                            <>
                                <div>
                                    <span className='vault__password--password__label'>{translation('NEW_SNMP_COMMUNITY_STRING')}:</span>
                                    <VaultPasswordComponent
                                        autoFocus={true}
                                        defaultStyle={true}
                                        formMode={FormMode.Add}
                                        vaultId={passwordDetails.VaultId}
                                        passwordId={passwordDetails.Id}
                                        passwordWanted={communityPassword}
                                        showPasswordStrength={true}
                                        showGenerateButton={true}
                                        returnGeneratedPassword={generateCommunityString}
                                        showPlaceholder={false}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className='passwordVaults vaults__table-passwords'>
                        {showGenericCreds && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('OLD_PASSWORD')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent vaultId={passwordDetails.VaultId} passwordId={passwordDetails.Id} />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_USERNAME')}:</span>
                                    <span className='vaults__table-passwords--password'>{passwordDetails.Username}</span>
                                </div>
                            </>
                        )}

                        {showMachineName && (
                            <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_MACHINE_NAME')}:</span>
                                <span className='vaults__table-passwords--description'>{passwordDetails.MachineName}</span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'SNMPV3' && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('OLD_AUTH_PASSWORD')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent vaultId={passwordDetails.VaultId} passwordId={passwordDetails.Id} passwordWanted={snmpAuthPassword} />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('OLD_PRIVACY_PASSWORD')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent vaultId={passwordDetails.VaultId} passwordId={passwordDetails.Id} passwordWanted={snmpPrivacyPassword} />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_USER')}:</span>
                                    <span className='vaults__table-passwords--password'>{passwordDetails.SnmpUser}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AGENT_PORT')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAgentPort}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PROFILE')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAuthenticationProfile}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_ENCRYPTION_PROTOCOL')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpEncryptionProtocol}</span>
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'SNMP' && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('OLD_SNMP_COMMUNITY_STRING')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent vaultId={passwordDetails.VaultId} passwordId={passwordDetails.Id} passwordWanted={communityPassword} />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AGENT_PORT')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAgentPort}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PROFILE')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAuthenticationProfile}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_ENCRYPTION_PROTOCOL')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpEncryptionProtocol}</span>
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'AD_Windows' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_DOMAIN')}:</span>
                                <span className='vaults__table-passwords--description'>{passwordDetails.Domain}</span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'Web' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_WEB')}:</span>
                                <span className='vaults__table-passwords--description'>{passwordDetails.WebsiteUrl}</span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'Database' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_DB_TYPE')}:</span>
                                {passwordDetails.DBType === 'Other' ? (
                                    <span className='vaults__table-passwords--description'>{passwordDetails.DBTypeOther}</span>
                                ) : (
                                    <span className='vaults__table-passwords--description'>{passwordDetails.DBType}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <Box ml={1} mt={2} className='source-credentials__checkbox'>
                        <PasslyCheckbox
                            name='confirmSourceChange'
                            checked={confirmSourceChange}
                            onChange={onCheckboxChange}
                            label={translation(confirmCredsChangedText)}
                        />
                    </Box>
                    {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}
                </div>
            )}

        </div>
    <ModalActionsComponent primaryBtn={translation('CONFIRM')} secondaryBtn={translation('CANCEL')} onClickPrimary={handleSaveBtn} onClickSecondary={handleCancel} disabled={!formValid} id='updatePassword' />

</>
    );
};
